import { parsePhoneNumber, isValidPhoneNumber } from 'libphonenumber-js';

/**
 * Validate US phone number using libphonenumber-js
 * @param {string} phoneNumber - Phone number to validate
 * @param {string} country - Country code (default: 'US')
 * @returns {boolean} - True if valid US phone number
 */
export const validateUSPhoneNumber = (phoneNumber, country = 'US') => {
  if (!phoneNumber) return false;
  try {
    return isValidPhoneNumber(phoneNumber, country);
  } catch (error) {
    console.error('Phone validation error:', error);
    return false;
  }
};

/**
 * Parse and format phone number to E.164 format
 * @param {string} phoneNumber - Phone number to format
 * @param {string} country - Country code (default: 'US')
 * @returns {string|null} - Formatted phone number or null if invalid
 */
export const formatPhoneNumber = (phoneNumber, country = 'US') => {
  if (!phoneNumber) return null;
  try {
    const parsed = parsePhoneNumber(phoneNumber, country);
    return parsed ? parsed.format('E.164') : null;
  } catch (error) {
    console.error('Phone formatting error:', error);
    return null;
  }
};

/**
 * Format phone number for display (national format)
 * @param {string} phoneNumber - Phone number to format
 * @param {string} country - Country code (default: 'US')
 * @returns {string|null} - Formatted phone number for display or null if invalid
 */
export const formatPhoneNumberForDisplay = (phoneNumber, country = 'US') => {
  if (!phoneNumber) return null;
  try {
    const parsed = parsePhoneNumber(phoneNumber, country);
    return parsed ? parsed.formatNational() : null;
  } catch (error) {
    console.error('Phone formatting error:', error);
    return null;
  }
};

/**
 * Get phone number type (MOBILE, FIXED_LINE, etc.)
 * @param {string} phoneNumber - Phone number
 * @param {string} country - Country code (default: 'US')
 * @returns {string|null} - Phone number type or null
 */
export const getPhoneNumberType = (phoneNumber, country = 'US') => {
  if (!phoneNumber) return null;
  try {
    const parsed = parsePhoneNumber(phoneNumber, country);
    return parsed ? parsed.getType() : null;
  } catch (error) {
    console.error('Phone type detection error:', error);
    return null;
  }
};

/**
 * Validate OTP - 6 digit code
 * @param {string} otp - OTP to validate
 * @returns {boolean} - True if valid OTP
 */
export const validateOTP = (otp) => {
  if (!otp) return false;
  // Remove any spaces or formatting
  const cleanOtp = otp.toString().replace(/\s/g, '').trim();
  // Check if it's exactly 6 digits
  return /^\d{6}$/.test(cleanOtp);
};

/**
 * Sanitize phone number input - removes common formatting characters
 * @param {string} phoneNumber - Phone number to sanitize
 * @returns {string} - Sanitized phone number
 */
export const sanitizePhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return '';
  return phoneNumber.replace(/[\s\-()\.]/g, '');
};

/**
 * Check if phone number looks like US number
 * @param {string} phoneNumber - Phone number to check
 * @returns {boolean} - True if it looks like a US phone number
 */
export const isLikelyUSPhoneNumber = (phoneNumber) => {
  if (!phoneNumber) return false;
  const cleaned = sanitizePhoneNumber(phoneNumber);
  // US phone numbers have 10 digits, optionally with country code +1
  return /^(\+1)?[2-9]\d{9}$/.test(cleaned);
};

/**
 * Validate that phone number starts with +1 (US country code)
 * @param {string} phoneNumber - Phone number to validate
 * @returns {boolean} - True if phone number starts with +1
 */
export const validatePhoneNumberStartsWithPlus1 = (phoneNumber) => {
  if (!phoneNumber) return false;
  return phoneNumber.trim().startsWith('+1');
};
